import { useEffect, useState } from "react";
import api, { formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", password: "", role: "staff", branch_id: "" };

export default function Users() {
  const { user, isSuperAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); if (isSuperAdmin) api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {}); }, [isSuperAdmin]);

  const branchName = (u) => {
    if (u.role === "super_admin" || u.role === "keuangan") return "Semua Cabang";
    if (!u.branch_id) return "—";
    const b = branches.find((x) => x.id === u.branch_id);
    return b ? `${b.name} (${b.code})` : "—";
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Pengguna ditambahkan");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="animate-fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Tim</p>
          <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Pengguna</h1>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-user-btn"
          className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover transition-colors duration-200">
          <Plus className="h-4 w-4" /> Tambah Pengguna
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
              <th className="px-6 py-4 font-semibold">Nama</th>
              <th className="px-6 py-4 font-semibold">Email</th>
              <th className="px-6 py-4 font-semibold">Role</th>
              {isSuperAdmin && <th className="px-6 py-4 font-semibold">Cabang</th>}
              <th className="px-6 py-4 font-semibold">Dibuat</th>
              <th className="px-6 py-4 text-right font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-4 font-medium text-brand-espresso">{u.name}</td>
                <td className="px-6 py-4 text-brand-muted">{u.email}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.role === "admin" ? "bg-brand-gold/20 text-brand-espresso" : "bg-brand-surface text-brand-muted"}`}>
                    {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                    {u.role}
                  </span>
                </td>
                {isSuperAdmin && (
                  <td className="px-6 py-4 text-brand-muted" data-testid={`user-branch-${u.id}`}>{branchName(u)}</td>
                )}
                <td className="px-6 py-4 text-brand-muted">{formatDate(u.created_at)}</td>
                <td className="px-6 py-4 text-right">
                  {u.id !== user?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`delete-user-${u.id}`} className="text-destructive hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus pengguna?</AlertDialogTitle>
                          <AlertDialogDescription>Akun "{u.name}" akan dihapus permanen.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(u.id)} className="bg-destructive hover:bg-destructive/90">Hapus</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Tambah Pengguna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-name-input" className="mt-1.5 focus-visible:ring-brand-gold" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input" className="mt-1.5 focus-visible:ring-brand-gold" />
            </div>
            <div>
              <Label>Kata Sandi</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-password-input" className="mt-1.5 focus-visible:ring-brand-gold" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="keuangan">Keuangan</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {isSuperAdmin && <SelectItem value="super_admin">Super Admin (Pusat)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && form.role !== "super_admin" && (
              <div>
                <Label>Cabang</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-branch-select"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy || !form.name || !form.email || !form.password} data-testid="save-user-btn"
              className="bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
